import { useMemo, useState } from 'react';
import type { LineSeries } from '../ui/line-chart';
import { LineChart } from '../ui/line-chart';
import { Select } from '../ui/select';
import { SkeletonText } from '../ui/skeleton';
import { usd } from '../../lib/graph-visuals';
import { seriesColor } from '../../lib/chart-colors';
import { lastNWeekStarts, realizedByWeek, realizedSince, validatedRecs } from '../../lib/savings';
import { useOrgDomainRecs } from './useOrgDomainRecs';
import { ExpandableChartShell } from './ExpandableChartShell';

type PeriodKey = '30' | '60' | '90' | 'all';
const PERIOD_OPTIONS = [
  { value: '30' as const, label: 'Last 30 days' },
  { value: '60' as const, label: 'Last 60 days' },
  { value: '90' as const, label: 'Last 90 days' },
  { value: 'all' as const, label: 'All time' },
];
const MAX_WEEKS = 104;

/** Number of weekly buckets to show for the selected window. */
function weekCount(
  period: PeriodKey,
  envs: { recs: Parameters<typeof validatedRecs>[0] }[],
): number {
  if (period !== 'all') return Math.ceil(Number(period) / 7);
  let earliest = Infinity;
  for (const e of envs) {
    for (const r of validatedRecs(e.recs))
      earliest = Math.min(earliest, new Date(r.validatedAt!).getTime());
  }
  if (!isFinite(earliest)) return 12;
  const weeks = Math.ceil((Date.now() - earliest) / (7 * 24 * 3600 * 1000)) + 1;
  return Math.min(MAX_WEEKS, Math.max(4, weeks));
}

const INFO = (
  <>
    <p>
      Newly <b>proven</b> monthly savings banked each week, per domain. A recommendation counts here
      the week it&apos;s validated — once we measure the resource actually got cheaper.
    </p>
    <p>Expand to change the window and toggle domains in the legend.</p>
  </>
);

/**
 * Realized-savings timeline (DASH-004/005): newly-proven monthly saving each week,
 * one line per domain. A lean preview that expands (click) into a full modal
 * with a window selector and a click-to-toggle legend.
 */
export function RealizedSavingsTrend() {
  const { envs, loaded } = useOrgDomainRecs();
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [period, setPeriod] = useState<PeriodKey>('90');

  const { series, hasData } = useMemo(() => {
    const weeks = lastNWeekStarts(new Date(), weekCount(period, envs));
    const ranked = envs
      .map((e) => ({ ...e, total: realizedSince(e.recs, weeks[0]!) }))
      .sort((a, b) => b.total - a.total);
    const series: LineSeries[] = ranked.map((e, i) => ({
      key: e.domainId,
      label: e.name,
      color: seriesColor(i, ranked.length),
      points: realizedByWeek(e.recs, weeks),
    }));
    return { series, hasData: ranked.some((e) => e.total > 0) };
  }, [envs, period]);

  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const visible = series.filter((s) => !hidden.has(s.key));

  return (
    <ExpandableChartShell title="Realized savings over time" info={INFO}>
      {(expanded) =>
        !loaded ? (
          <SkeletonText lines={4} lineClassName="h-4" />
        ) : !hasData ? (
          <p className="py-6 text-center text-sm text-muted">
            No realized savings yet — validated recommendations chart here week by week.
          </p>
        ) : (
          <>
            {expanded && (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {series.map((s) => {
                    const off = hidden.has(s.key);
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => toggle(s.key)}
                        className={`flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[0.7rem] transition-colors ${
                          off
                            ? 'border-hairline text-muted line-through'
                            : 'border-hairline text-haze hover:text-bone'
                        }`}
                      >
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: off ? 'var(--muted)' : s.color }}
                        />
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                <Select
                  value={period}
                  onChange={(v) => setPeriod(v as PeriodKey)}
                  ariaLabel="Time window"
                  size="sm"
                  options={PERIOD_OPTIONS}
                />
              </div>
            )}
            <LineChart
              series={expanded ? visible : series}
              height={expanded ? 300 : 150}
              formatValue={(n) => `${usd(n)}/mo`}
            />
          </>
        )
      }
    </ExpandableChartShell>
  );
}
